import { useCallback, useRef, useState } from "react";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import {
	getYearlyExtendedUtilisationReport,
} from "~/modules/reports/api/utilisationReport/utilisationReportApiDispatchers.ts";

import { buildUtilisationWorkbook } from "./buildUtilisationWorkbook";

export type ExportStatus = "idle" | "fetching" | "generating" | "done" | "error";

export function useExtendedUtilisationExcelReport(year: number) {
	const [status, setStatus] = useState<ExportStatus>("idle");
	const [error, setError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	const startExport = useCallback(async () => {
		if (status !== "idle") return;

		setError(null);
		setStatus("fetching");

		const controller = new AbortController();
		abortRef.current = controller;

		try {
			const response = await getYearlyExtendedUtilisationReport(String(year), {
				signal: controller.signal,
			});

			const data = getDataFromResponse(response);

			setStatus("generating");

			// Yield to the UI so the status change renders before the blocking workbook build
			await new Promise((resolve) => setTimeout(resolve, 0));

			const buffer = await buildUtilisationWorkbook(data);

			const blob = new Blob([buffer], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `Auslastung_${year}.xlsx`;
			a.click();
			URL.revokeObjectURL(url);

			setStatus("done");
		} catch (e) {
			if (controller.signal.aborted) return;
			setError(e instanceof Error ? e.message : "Export failed");
			setStatus("error");
		} finally {
			abortRef.current = null;
		}
	}, [year, status]);

	const cancelExport = useCallback(() => {
		abortRef.current?.abort();
		abortRef.current = null;
		setStatus("idle");
		setError(null);
	}, []);

	const reset = useCallback(() => {
		setStatus("idle");
		setError(null);
	}, []);

	return { status, error, startExport, cancelExport, reset };
}